import { ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import i18n from 'i18next';
import LocalStorage from 'utils/bom-dom-manipulation/local-storage';
import { casinoEnabled } from 'utils/constants/app/general';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { storageKeyName } from 'utils/generic/storage-key-name';
import {
  IAccountSettingsMenu,
  IAccountSettingsMenuChild,
  IAccountSettingsMenuChildWithForm
} from 'interfaces/account-settings-menu';
import { GlobalIcon } from 'components/GlobalIcon';
import { Badge } from 'newcomponents/UI/Badge';
import { getBonuses } from 'store/selectors/user-data';
import './index.less';

const isLoyaltyPointsAvailable =
  LocalStorage.getItem(
    storageKeyName('account', 'LOYALTY_POINTS_AVAILABLE')
  ) === 'true';

const BonuseCounter = (): ReactElement | null => {
  const bonuses = useSelector(getBonuses);
  const [bonusesCount, setBonusesCount] = useState<number>();

  useEffect(() => {
    setBonusesCount(
      (bonuses?.casinoBonuses?.length || 0) +
        (bonuses?.freeBonuses?.length || 0) +
        (bonuses?.freeSpinBonuses?.length || 0)
    );
  }, [bonuses]);

  return bonusesCount ? (
    <Badge
      status="error"
      className="profileInfo__bonuses__badge profileInfo__badge"
      text={`${bonusesCount} ${i18n.t(`account.bonuses`)}`}
    />
  ) : null;
};

export const VERIFICATION_SETTINGS_SECTION = {
  key: 'accounts:settings:verification-settings',
  name: i18n.t('account.verificationSettings'),
  icon: (
    <GlobalIcon
      lib="account"
      name="verificationSettings"
      theme="default"
      size={EIconSize._24}
    />
  ),
  query: { accounts: '*', settings: '*', 'verification-settings': '*' },
  hidden: false,
  formType: 'verificationSettings'
};

export const ACCOUNT_SETTINGS_MENU: IAccountSettingsMenuChildWithForm[] = [
  {
    key: 'accounts:settings:profile',
    name: i18n.t('account.myProfile'),
    icon: (
      <GlobalIcon
        lib="account"
        name="accountSettingsOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', profile: '*' },
    hidden: false,
    formType: 'tabEditProfile'
  },
  {
    key: 'accounts:settings:password',
    name: i18n.t('account.changePassword'),
    icon: (
      <GlobalIcon
        lib="account"
        name="changePasswordOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', password: '*' },
    hidden: false,
    formType: 'tabChangePassword'
  },
  {
    key: 'accounts:settings:documents',
    name: i18n.t('account.documents'),
    icon: (
      <GlobalIcon
        lib="account"
        name="documentsOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', documents: '*' },
    hidden: false,
    formType: 'tabDocuments'
  },
  {
    key: 'accounts:settings:limits',
    name: i18n.t('account.limits'),
    icon: (
      <GlobalIcon
        lib="account"
        name="limitsOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', limits: '*' },
    hidden: false,
    formType: 'tabBetLimit'
  },
  {
    key: 'accounts:settings:self-exclusion',
    name: i18n.t('account.self-exclusion'),
    icon: (
      <GlobalIcon
        lib="account"
        name="selfExclusionOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', 'self-exclusion': '*' },
    hidden: false,
    formType: 'tabSelfExclusion'
  },
  {
    key: 'accounts:settings:account-status',
    name: i18n.t('account.account-status'),
    icon: (
      <GlobalIcon
        lib="account"
        name="personalCardOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', 'account-status': '*' },
    hidden: false,
    formType: 'accountStatus'
  },
  {
    key: 'accounts:settings:timeout',
    name: i18n.t('account.timeOut'),
    icon: (
      <GlobalIcon
        lib="account"
        name="timeOutOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', timeout: '*' },
    hidden: false,
    formType: 'tabTimeOut'
  },
  {
    key: 'accounts:settings:reality-check',
    name: i18n.t('account.realityCheck'),
    icon: (
      <GlobalIcon
        lib="account"
        name="realityCheckOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', 'reality-check': '*' },
    hidden: false,
    formType: 'sessionTime'
  },
  {
    key: 'accounts:settings:notification-settings',
    name: i18n.t('account.notificationSettings'),
    icon: (
      <GlobalIcon
        lib="account"
        name="notificationSettingsOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', 'notification-settings': '*' },
    hidden: false,
    formType: 'tabNotifications'
  },
  {
    key: 'accounts:settings:two-factor-auth',
    name: i18n.t('account.two-factor-authentication'),
    icon: (
      <GlobalIcon
        lib="account"
        name="twoFactorAuthenticationOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', 'two-factor-auth': '*' },
    hidden: false,
    formType: 'twoFactorAuthentication'
  },
  {
    key: 'accounts:settings:accumulated-profit',
    name: i18n.t('account.accumulatedProfit'),
    icon: (
      <GlobalIcon
        lib="account"
        name="accumulatedProfitWalletOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', 'accumulated-profit': '*' },
    hidden: false,
    formType: 'tabAccumulatedLosses'
  },
  {
    key: 'accounts:settings:verify-account',
    name: i18n.t('account.verify-account'),
    icon: (
      <GlobalIcon
        lib="account"
        name="verifyAccountOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', 'verify-account': '*' },
    hidden: false,
    formType: 'tabVerifyAccount'
  },
  {
    key: 'accounts:settings:gamstop',
    name: i18n.t('account.gamstop'),
    icon: (
      <GlobalIcon
        lib="account"
        name="gamstopOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', gamstop: '*' },
    hidden: false,
    formType: 'tabGamstop'
  },
  {
    key: 'accounts:settings:login-limit',
    name: i18n.t('account.loginLimit'),
    icon: (
      <GlobalIcon
        lib="account"
        name="loginLimitOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', 'login-limit': '*' },
    hidden: false,
    formType: 'loginLimit'
  },
  {
    key: 'accounts:settings:sessions',
    name: i18n.t('account.my-sessions'),
    icon: (
      <GlobalIcon
        lib="account"
        name="mySessionsOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', sessions: '*' },
    hidden: false,
    formType: 'tabLoginHistory'
  },
  ...(SpringConfigs.SHOW_ACCOUNT_SESSION
    ? [
        {
          key: 'accounts:settings:session-information',
          name: i18n.t('account.session-information'),
          icon: (
            <GlobalIcon
              lib="account"
              name="sessionInfo"
              theme="default"
              size={EIconSize._24}
            />
          ),
          query: { accounts: '*', settings: '*', 'session-information': '*' },
          hidden: false,
          formType: 'current-session-info'
        }
      ]
    : []),
  {
    key: 'accounts:settings:safer-gambling',
    name: i18n.t('account.saferGambling'),
    icon: (
      <GlobalIcon
        lib="account"
        name="saferGambling"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', 'safer-gambling': '*' },
    hidden: false,
    formType: 'saferGambling'
  },
  {
    key: 'accounts:settings:availability-period',
    name: i18n.t('account.availabilityPeriod'),
    icon: (
      <GlobalIcon
        lib="account"
        name="availabilityPeriod"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', settings: '*', 'availability-period': '*' },
    hidden: false,
    formType: 'availability-period'
  }
];

const WALLET_MENU: IAccountSettingsMenuChild[] = [
  {
    key: 'accounts:wallet:deposit',
    name: i18n.t('account.deposit'),
    icon: (
      <GlobalIcon
        lib="account"
        name="depositOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', wallet: '*', deposit: '*' },
    hidden: false
  },
  {
    key: 'accounts:wallet:withdraw',
    name: i18n.t('account.withdraw'),
    icon: (
      <GlobalIcon
        lib="account"
        name="withdrawOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', wallet: '*', withdraw: '*' },
    hidden: false
  },
  {
    key: 'accounts:wallet:stake-and-earn',
    name: i18n.t('account.stake-and-earn'),
    icon: (
      <GlobalIcon
        lib="account"
        name="stakeandearnOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', wallet: '*', 'stake-and-earn': '*' },
    hidden: false
  },
  {
    key: 'accounts:wallet:balance-history',
    name: i18n.t('account.balance-history'),
    icon: (
      <GlobalIcon
        lib="account"
        name="documentsOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', wallet: '*', 'balance-history': '*' },
    hidden: false
  },
  {
    key: 'accounts:wallet:my-cards',
    name: i18n.t('account.myCards'),
    icon: (
      <GlobalIcon
        lib="account"
        name="myCardsOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', wallet: '*', 'my-cards': '*' },
    alias: 'myCards',
    hidden: false
  },
  ...(SpringConfigs.SHOW_ACCOUNT_STATEMENT
    ? [
        {
          key: 'accounts:wallet:statement',
          name: i18n.t('account.statement'),
          icon: (
            <GlobalIcon
              lib="account"
              name="statementOutlined"
              theme="default"
              size={EIconSize._24}
            />
          ),
          query: { accounts: '*', wallet: '*', statement: '*' },
          alias: 'statement',
          hidden: false
        }
      ]
    : []),
  {
    key: 'accounts:wallet:bank-account',
    name: i18n.t('account.bankAccount'),
    icon: (
      <GlobalIcon
        lib="account"
        name="bankAccount"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', wallet: '*', 'bank-account': '*' },
    hidden: false
  },
  {
    key: 'accounts:wallet:transfer-to-friend',
    name: i18n.t('account.transferToFriend'),
    icon: (
      <GlobalIcon
        lib="generic"
        name="transfer"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', wallet: '*', 'transfer-to-friend': '*' },
    hidden: false
  }
];

export const BONUSES_MENU: IAccountSettingsMenuChild[] = [
  {
    key: 'accounts:bonuses:bonus',
    name: i18n.t('account.bonuses'),
    icon: (
      <GlobalIcon
        lib="account"
        name="bonusesOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    extra: <BonuseCounter />,
    query: { accounts: '*', bonuses: '*', bonus: '*' },
    hidden: false
  },
  {
    key: 'accounts:bonuses:refer-friend',
    name: i18n.t('account.referFriend'),
    icon: (
      <GlobalIcon
        lib="account"
        name="referFriendOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', bonuses: '*', 'refer-friend': '*' },
    hidden: false
  },
  {
    key: 'accounts:bonuses:management',
    name: i18n.t('account.bonusManagement'),
    icon: (
      <GlobalIcon
        lib="generic"
        name="maximizeAlt"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', bonuses: '*', management: '*' },
    hidden: false
  }
];

export const ACCOUNT_DESKTOP_MENU: IAccountSettingsMenu[] = [
  {
    key: 'accounts:login',
    name: i18n.t('account.login'),
    icon: (
      <GlobalIcon
        lib="account"
        name="changePasswordOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    auth: false,
    query: { accounts: '*', login: '*' },
    hidden: false,
    formType: 'login'
  },
  {
    key: 'accounts:register',
    name: i18n.t('account.register'),
    icon: (
      <GlobalIcon
        lib="account"
        name="changePasswordOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    auth: false,
    query: { accounts: '*', register: '*' },
    hidden: false,
    formType: 'register'
  },
  {
    key: 'accounts:settings',
    name: i18n.t('account.accountSettings'),
    icon: (
      <GlobalIcon
        lib="account"
        name="accountSettingsOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    auth: true,
    query: { accounts: '*', settings: '*', profile: '*' },
    queryMobile: { accounts: '*', settings: '*', menu: '*' },
    children: ACCOUNT_SETTINGS_MENU,
    hidden: false,
    formType: 'myProfile'
  },
  {
    key: 'accounts:wallet',
    name: i18n.t('account.wallet'),
    auth: true,
    icon: (
      <GlobalIcon
        lib="account"
        name="walletOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', wallet: '*', deposit: '*' },
    children: WALLET_MENU,
    hidden: false,
    formType: 'myWallet'
  },
  {
    key: 'accounts:bonuses',
    name: i18n.t('account.bonuses'),
    auth: true,
    icon: (
      <GlobalIcon
        lib="account"
        name="bonusesOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', bonuses: '*', bonus: '*' },
    queryMenu: { accounts: '*', bonuses: '*', menu: '*' },
    children: BONUSES_MENU,
    hidden: false,
    formType: 'bonuses'
  },
  {
    key: 'accounts:bonus-iframe',
    name: i18n.t('account.bonus-iframe'),
    auth: true,
    icon: (
      <GlobalIcon
        lib="account"
        name="vip"
        theme="default"
        color="#A57B1B"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', 'bonus-iframe': '*' },
    hidden: false,
    formType: 'accountBonusIframe'
  },
  ...(isLoyaltyPointsAvailable
    ? [
        {
          key: 'accounts:loyalty-points',
          name: i18n.t('account.loyaltyPoints'),
          auth: true,
          icon: (
            <GlobalIcon
              lib="account"
              name="medalOutlined"
              theme="default"
              size={EIconSize._24}
            />
          ),
          query: { accounts: '*', 'loyalty-points': '*' },
          hidden: false,
          formType: 'loyalty-points'
        }
      ]
    : []),
  {
    key: 'accounts:messages',
    name: i18n.t('account.messages'),
    auth: true,
    icon: (
      <GlobalIcon
        lib="account"
        name="messagesOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', messages: '*' },
    hidden: false,
    formType: 'messages'
  },
  ...(!casinoEnabled
    ? [
        {
          key: 'accounts:bet-history',
          name: i18n.t('account.betHistory'),
          auth: true,
          icon: (
            <GlobalIcon
              lib="account"
              name="betHistoryOutlined"
              theme="default"
              size={EIconSize._24}
            />
          ),
          query: { accounts: '*', 'bet-history': '*' },
          hidden: false,
          formType: 'myBets'
        }
      ]
    : []),
  {
    key: 'accounts:journey',
    name: i18n.t('account.customerJourney'),
    auth: true,
    icon: (
      <GlobalIcon
        lib="account"
        name="customerJourney"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', journey: '*' },
    hidden: false,
    formType: 'customerJourney'
  },
  {
    key: 'accounts:rewards',
    name: i18n.t('account.rewards'),
    auth: true,
    icon: (
      <GlobalIcon
        lib="account"
        name="rewardsOutlined"
        theme="default"
        size={EIconSize._24}
      />
    ),
    query: { accounts: '*', rewards: '*' },
    hidden: false,
    formType: 'rewards'
  }
];
